import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Paperclip, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { PaginationBar, usePagedList, CardListSkeleton as ListSkeleton } from "../../components/PaginationBar";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { api, errMsg, fmtDate, fileHref } from "../../lib/api";
import { StaffLabel, staffLabelText } from "../../components/StaffAvatar";

const GRIEVANCE_STATUSES = ["open", "in_progress", "escalated", "resolved", "closed"];

export default function GrievancesAdmin() {
  const { code } = useParams();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Phase 133: the list loads only on Fetch, with the search / status applied at that moment
  const gate = useFetchGate({ q: q.trim(), status: statusFilter });
  const search = gate.applied?.q || "";
  const appliedStatus = gate.applied?.status || "all";
  const list = usePagedList(`/t/${code}/grievances`, {
    params: { search: search || undefined, status: appliedStatus === "all" ? undefined : appliedStatus },
    enabled: gate.fetched, reloadKey: gate.version,
  });
  const items = list.data ? list.items : null;
  const clearAll = () => { setQ(""); setStatusFilter("all"); gate.reset(); };
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("in_progress");
  const [remarks, setRemarks] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);

  const load = list.reload;
  useEffect(() => {
    api.get(`/t/${code}/users`).then((r) =>
      setStaff(r.data.filter((u) => ["municipal_admin", "supervisor", "tax_collector"].includes(u.role) && u.is_active))
    ).catch(() => {});
  }, [code]);

  const refreshSelected = async (id) => {
    await list.reload();
    const r = await api.get(`/t/${code}/grievances`, { params: { page: list.page, limit: list.limit, search: search || undefined,
      status: appliedStatus === "all" ? undefined : appliedStatus } });
    const cur = (r.data.items || []).find((x) => x.id === id);
    if (cur) setSelected(cur);
  };

  const update = async () => {
    setBusy(true);
    try {
      await api.put(`/t/${code}/grievances/${selected.id}/status`, { status: newStatus, remarks });
      toast.success("Grievance updated");
      setSelected(null);
      setRemarks("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!assignee) return toast.error("Select a staff member");
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/grievances/${selected.id}/assign`, { user_id: assignee });
      toast.success(`Assigned to ${r.data.assigned_to_name}`);
      setAssignee("");
      await refreshSelected(selected.id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Grievances" subtitle="Citizen complaints with SLA tracking, assignment and resolution" />
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-center gap-2 mb-4" testId="grievances-filter-bar">
        <Input placeholder="Search ticket, subject, citizen…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-10 max-w-xs" data-testid="grievances-search-input" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-[160px]" data-testid="grievances-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {GRIEVANCE_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <FetchButton type="submit" size="sm" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched} testId="grievances-fetch-button" />
        {(q || statusFilter !== "all") && (
          <Button type="button" variant="ghost" size="sm" className="h-10" onClick={clearAll} data-testid="grievances-clear-filters">Clear</Button>
        )}
        <FetchDirtyHint dirty={gate.dirty} testId="grievances-fetch-dirty" />
      </FetchForm>
      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="grievances-fetch-prompt"
            subtitle="Search by ticket, subject or citizen, or pick a status above, then click Fetch to load the grievances." />
        </CardContent></Card>
      ) : list.error && !items ? (
        <EmptyState title="Could not load grievances" subtitle={errMsg(list.error)}
          action={<Button variant="outline" size="sm" onClick={list.reload} data-testid="grievances-retry">Retry</Button>} />
      ) : !items ? <ListSkeleton /> : items.length === 0 ? (
        <EmptyState title={list.total === 0 && !search && appliedStatus === "all" ? "No grievances" : "No grievances match these filters"} />
      ) : (
        <div className={`space-y-3 ${list.loading ? "opacity-60 transition-opacity" : ""}`} data-testid="grievances-admin-list">
          {items.map((g) => (
            <Card key={g.id} className="shadow-sm">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-sm font-medium">{g.ticket_number}</p>
                    <StatusBadge status={g.status} />
                    <StatusBadge status={g.priority} className="capitalize" />
                    {g.assigned_to_name && (
                      <Badge variant="outline" className="text-[10px]"><UserCheck className="h-3 w-3 mr-1" /><StaffLabel code={code} userId={g.assigned_to} name={g.assigned_to_name} /></Badge>
                    )}
                    {(g.attachments || []).length > 0 && (
                      <Badge variant="secondary" className="text-[10px]"><Paperclip className="h-3 w-3 mr-1" />{g.attachments.length}</Badge>
                    )}
                  </div>
                  <p className="font-medium text-sm mt-1">{g.subject}</p>
                  <p className="text-xs text-muted-foreground">{g.category} • by {g.citizen_name} • {fmtDate(g.created_at)} • SLA {fmtDate(g.sla_deadline)}</p>
                </div>
                <Button size="sm" variant={["resolved", "closed"].includes(g.status) ? "outline" : "default"}
                  onClick={() => { setSelected(g); setNewStatus(g.status === "open" ? "in_progress" : "resolved"); }}
                  data-testid={`grievance-update-${g.ticket_number}`}>
                  {["resolved", "closed"].includes(g.status) ? "View" : "Manage"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {items && list.total > 0 && (
        <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
          onPageChange={list.setPage} onLimitChange={list.setLimit} testId="grievances-pagination" />
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{selected?.ticket_number}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm">{selected.description}</p>

              {(selected.attachments || []).length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {selected.attachments.map((a, i) => (
                    <a key={i} href={fileHref(a.url)} target="_blank" rel="noreferrer"
                      className="text-xs flex items-center gap-1 border rounded-full px-2.5 py-1 hover:bg-muted"
                      data-testid={`admin-grievance-attachment-${i}`}>
                      <Paperclip className="h-3 w-3" /> {a.filename || `Attachment ${i + 1}`}
                    </a>
                  ))}
                </div>
              )}

              <div>
                <p className="text-sm font-medium mb-1">Timeline</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {(selected.timeline || []).map((t, i) => (
                    <p key={i} className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground capitalize">{t.status.replace(/_/g, " ")}</span> • {t.remarks || "—"} • by {t.by} • {fmtDate(t.at)}
                    </p>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><UserCheck className="h-4 w-4" /> Assignment
                  {selected.assigned_to_name && <Badge variant="outline" className="text-[10px] ml-1"><StaffLabel code={code} userId={selected.assigned_to} name={selected.assigned_to_name} /></Badge>}
                </p>
                <div className="flex gap-2">
                  <Select value={assignee} onValueChange={setAssignee}>
                    <SelectTrigger data-testid="grievance-assignee-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                    <SelectContent>
                      {staff.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{staffLabelText(u.name, u.employee_id)} · {u.role.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={assign} disabled={busy} data-testid="grievance-assign-button">Assign</Button>
                </div>
              </div>

              {!["resolved", "closed"].includes(selected.status) || selected.status === "resolved" ? (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label>New status</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="grievance-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["in_progress", "escalated", "resolved", "closed"].map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Remarks</Label>
                    <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="grievance-remarks-input" />
                  </div>
                  <Button className="w-full" onClick={update} disabled={busy} data-testid="grievance-update-submit">Update grievance</Button>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
